import { ModalBody, Stack, Text } from '@chakra-ui/react';
import * as React from 'react';
import { AllSearchResults, connectAutoComplete } from 'react-instantsearch-core';

import { sortGlobalSearchSectionsByRelevance } from '@/components/GlobalSearch/utils/sortGlobalSearchSections';
import { useGlobalSearchContext } from '@/imports/ui/components/search/SearchProvider';

import { OpenSearchEvent } from './components/Event';
import { OpenSearchFile } from './components/File';
import { OpenSearchOrganization } from './components/Organization';
import { OpenSearchPost } from './components/Post';
import { OpenSearchUser } from './components/User';
import { OpenSearchHit } from './types';

export const OpenSearchResults = connectAutoComplete<
  AllSearchResults<OpenSearchHit>
>((props) => {
  const { indexData } = useGlobalSearchContext();

  if (!indexData || !props.hits.some(({ hits }) => hits.length)) {
    return null;
  }

  const labels: Record<string, string> = {
    [indexData.memberIndexName]: 'Members',
    [indexData.orgIndexName]: 'Organizations',
    [indexData.eventIndexName]: 'Events',
    [indexData.fileIndexName]: 'Files',
    [indexData.postIndexName]: 'Posts',
  };

  const fallbackPriority: Record<string, number> = {
    [indexData.memberIndexName]: 1,
    [indexData.orgIndexName]: 2,
    [indexData.eventIndexName]: 3,
    [indexData.fileIndexName]: 4,
    [indexData.postIndexName]: 5,
  };

  const orderedSections = sortGlobalSearchSectionsByRelevance(
    props.hits,
    fallbackPriority
  );

  return (
    <ModalBody borderTop="1px" borderBottom="1px" borderColor="gray.200">
      <Stack>
        {orderedSections.map(({ index, hits }) => {
          if (!hits.length) {
            return null;
          }

          return (
            <Stack key={index}>
              <Text
                textStyle="detailStrong"
                textTransform="uppercase"
                color="gray.500"
              >
                {labels[index] ?? index}
              </Text>

              {hits.map((hit) => {
                if (index === indexData.postIndexName) {
                  return (
                    <OpenSearchPost
                      key={hit.objectID}
                      hit={hit as OpenSearchHit}
                    />
                  );
                }

                if (index === indexData.fileIndexName) {
                  return (
                    <OpenSearchFile
                      key={hit.objectID}
                      hit={hit as OpenSearchHit}
                    />
                  );
                }

                if (index === indexData.memberIndexName) {
                  return (
                    <OpenSearchUser
                      key={hit.objectID}
                      hit={hit as OpenSearchHit}
                      viewableAttributeField={
                        indexData.memberIndexViewableField
                      }
                    />
                  );
                }

                if (index === indexData.eventIndexName) {
                  return (
                    <OpenSearchEvent
                      key={hit.objectID}
                      hit={hit as OpenSearchHit}
                    />
                  );
                }

                if (index === indexData.orgIndexName) {
                  return (
                    <OpenSearchOrganization
                      key={hit.objectID}
                      hit={hit as OpenSearchHit}
                    />
                  );
                }

                return null;
              })}
            </Stack>
          );
        })}
      </Stack>
    </ModalBody>
  );
});
